import { useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getActiveViewQueryDocument } from "./active-view.gql";

export interface UseGetActiveViewProps {
  key?: string;
  workspaceId?: string;
  enabled?: boolean;
}

export const useGetActiveView = ({ key, workspaceId, enabled = true }: UseGetActiveViewProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.activeViews.detail(key ?? "", workspaceId ?? ""),
    queryFn: async () => {
      if (!key || !workspaceId) {
        throw new Error("Key and workspace ID are required");
      }
      const client = createOperationClient("GetActiveView");
      const data = await client.request(getActiveViewQueryDocument, {
        input: { key, workspaceId },
      });
      return data.activeView;
    },
    enabled: enabled && !!key && !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual items
  });

  const activeView = useMemo(() => {
    return queryResult.data ?? null;
  }, [queryResult.data]);

  return {
    activeView,
    loading: queryResult.isLoading,
    error: queryResult.error,
    queryResult,
  };
};
